import React, { FC, useEffect, useMemo, useState } from "react";
import { Table } from "react-bootstrap";
import { debounce } from "lodash";
import {
  CustomButton,
  Loader,
  PaginationModel,
  SingleSelect,
  TableData,
  TableHead,
  TableLayout,
  TablePagination,
  TableRow,
  TBody,
  THead,
} from "@mindmemobile/mm-shared-global";
import { MerchantAccountService } from "../../shared/services";
import {
  IAPIAgencySummaryModel,
  IAPIMerchantAccountSummaryModel,
} from "../../shared/interfaces";
import { ApplicationStatus, MerchantAccountsGateway } from "../../shared/enums";
import CustomPlaceholders from "../../shared/components/CustomPlaceholders";
import DollarIcon from "../../../../../images/placeholders/dollar-circle-muted-grey.svg";
import AdminAuthStore from "../../shared/store/AdminAuthStore";

const PAGE_SIZE = 25;

// Same palette as MerchantAccounts.tsx, kept consistent across the app.
const getStatusStyles = (status: ApplicationStatus) => {
  switch (status) {
    case ApplicationStatus.Pending:
      return { backgroundColor: "#FFF5F8", color: "#DE5F85" };
    case ApplicationStatus.Active:
      return { backgroundColor: "#EEF7EA", color: "#2ECC71" };
    case ApplicationStatus.Inactive:
      return { backgroundColor: "#F9F9F9", color: "#95A5A6" };
    case ApplicationStatus.InReview:
      return { backgroundColor: "#ECF0FF", color: "#6A7AB5" };
    case ApplicationStatus.Declined:
    case ApplicationStatus.Cancelled:
      return { backgroundColor: "#FDEDEC", color: "#E74C3C" };
    default:
      return { backgroundColor: "#EEEEEE", color: "#555555" };
  }
};

const getGatewayStyles = (gateway: keyof typeof MerchantAccountsGateway) => {
  switch (gateway) {
    case MerchantAccountsGateway.Fiserv:
      return { backgroundColor: "#EAF3FC", color: "#3178C6" };
    case MerchantAccountsGateway.NMI:
      return { backgroundColor: "#F3EAFC", color: "#8E44AD" };
    default:
      return { backgroundColor: "#EEEEEE", color: "#555555" };
  }
};

const renderBadge = (styles: { backgroundColor: string; color: string }, label: string) => (
  <CustomButton bgColor={styles.backgroundColor} color={styles.color} fontWeight="600 !important">
    {label}
  </CustomButton>
);

// Explicit UTC components (not toLocaleString, which would silently render in the
// viewer's local timezone despite the "(UTC)" column header) - keeps the label accurate.
const formatUtcDate = (isoDate: string) => {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) return "";
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${pad(date.getUTCMonth() + 1)}/${pad(date.getUTCDate())}/${date.getUTCFullYear()} ${pad(
    date.getUTCHours()
  )}:${pad(date.getUTCMinutes())}`;
};

const AdminParentAccountsGrid: FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState<IAPIMerchantAccountSummaryModel[]>([]);
  const [agencies, setAgencies] = useState<IAPIAgencySummaryModel[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [agencyId, setAgencyId] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [pagination, setPagination] = useState<PaginationModel | null>(null);
  const [error, setError] = useState("");
  // The admin route can mount from the iframe's initial URL before Main_Web2's postMessage
  // carrying the AdminApiKey arrives - wait for it rather than firing requests that 401.
  const [adminApiKey, setAdminApiKeyState] = useState(AdminAuthStore.adminApiKey);

  useEffect(() => AdminAuthStore.subscribe(setAdminApiKeyState), []);

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchValue(value);
        setPageNumber(1);
      }, 400),
    []
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  useEffect(() => {
    if (!adminApiKey) return;

    MerchantAccountService.getMerchantAccountAgencies().subscribe({
      next: (response) => setAgencies(response.result || []),
      error: (err) => console.log("Error loading agencies", err),
    });
  }, [adminApiKey]);

  useEffect(() => {
    if (!adminApiKey) return;

    setIsLoading(true);
    setError("");

    MerchantAccountService.getMerchantAccountsSummary({
      pageNumber,
      pageSize,
      searchValue,
      agencyId,
    }).subscribe({
      next: (response) => {
        setRows(response.result || []);
        setPagination(PaginationModel.deserialize(response.meta));
        setIsLoading(false);
      },
      error: (err) => {
        console.log("Error loading admin merchant accounts grid", err);
        setError("Failed to load merchant accounts.");
        setIsLoading(false);
      },
    });
  }, [adminApiKey, pageNumber, pageSize, searchValue, agencyId]);

  const onSearchChange = (value: string) => {
    setSearchInput(value);
    debouncedSearch(value);
  };

  const onClearSearch = () => {
    debouncedSearch.cancel();
    setSearchInput("");
    setSearchValue("");
    setPageNumber(1);
  };

  const onAgencyChange = (value: string) => {
    setAgencyId(value || "");
    setPageNumber(1);
  };

  const agencyOptions = useMemo(
    () => [
      { label: "All Agencies", value: "" },
      ...[...agencies]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((agency) => ({ label: agency.name, value: agency.id })),
    ],
    [agencies]
  );

  return (
    <div style={{ padding: "20px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
          gap: "12px",
        }}
      >
        <h3 style={{ margin: 0 }}>Merchant Accounts</h3>
        <div style={{ display: "flex", gap: "12px" }}>
          <SingleSelect
            options={[agencyOptions]}
            nameKeys={["label"]}
            valueKeys={["value"]}
            selectedKey={agencyId}
            emptySelection="All Agencies"
            searchable
            searchPlaceholder="Search agencies"
            onChange={(option) => onAgencyChange(option?.value)}
            width="240px"
            className="default-padding-search-dpdn"
          />
          <div style={{ position: "relative", width: "360px", maxWidth: "100%" }}>
            <input
              type="text"
              placeholder="Search by account, business, sub account or merchant name"
              value={searchInput}
              onChange={(e) => onSearchChange(e.target.value)}
              style={{ padding: "8px 32px 8px 12px", width: "100%" }}
            />
            {searchInput && (
              <button
                type="button"
                onClick={onClearSearch}
                aria-label="Clear search"
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  border: "none",
                  background: "transparent",
                  color: "#95A5A6",
                  fontSize: "16px",
                  lineHeight: 1,
                  cursor: "pointer",
                  padding: "4px",
                }}
              >
                &times;
              </button>
            )}
          </div>
        </div>
      </div>
      {error && <div className="text-danger">{error}</div>}
      {isLoading ? (
        <>{new Loader(true).loaderComponent}</>
      ) : rows.length === 0 ? (
        <CustomPlaceholders
          image={DollarIcon}
          label="No Merchant Accounts Found"
          description={
            searchValue || agencyId
              ? "Try a different search term or agency filter."
              : "Merchant accounts will appear here once accounts sign up for payments."
          }
          height="360px"
        />
      ) : (
        <TableLayout>
          <Table className="table-main">
            <THead>
              <TableHead>Account Name</TableHead>
              <TableHead>Business Name</TableHead>
              <TableHead>Sub Account</TableHead>
              <TableHead>Merchant Account Name</TableHead>
              <TableHead>Gateway</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created Date (UTC)</TableHead>
            </THead>
            <TBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableData title={row.parentAccountName}>{row.parentAccountName}</TableData>
                  <TableData title={row.businessName}>{row.businessName}</TableData>
                  <TableData title={row.subAccountName}>{row.subAccountName}</TableData>
                  <TableData title={row.name}>{row.name}</TableData>
                  <TableData style={{ overflow: "visible" }} title={row.gateway}>
                    {renderBadge(getGatewayStyles(row.gateway), row.gateway)}
                  </TableData>
                  <TableData style={{ overflow: "visible" }} title={row.applicationStatus}>
                    {renderBadge(getStatusStyles(row.applicationStatus), row.applicationStatus)}
                  </TableData>
                  <TableData title={formatUtcDate(row.createdDate)}>{formatUtcDate(row.createdDate)}</TableData>
                </TableRow>
              ))}
            </TBody>
          </Table>
          {pagination && (
            <TablePagination
              pagination={pagination}
              onPageChange={(page: number) => setPageNumber(page)}
              onPageSizeChange={(size: number) => {
                setPageSize(size);
                setPageNumber(1);
              }}
              className="table-pagination"
            />
          )}
        </TableLayout>
      )}
    </div>
  );
};

export default AdminParentAccountsGrid;
